// @flow
import React, { useEffect, useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { confirm, showAlert } from 'actions/app';
import { StatusManager } from 'common/components/StatusManager';
import { useFetchWithStatus } from 'hooks/fetchWithStatus';
import { EntityRoutes } from 'routes/constants';
import { LinkHiringAuthorityOrCandidate } from 'services/Candidates';
import strings from 'strings';
import type { DrawerUiState } from 'types/app';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import { buildCloseAndSaveButtonDefinition } from 'UI/components/templates/FPDrawer';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { Endpoints } from 'UI/constants/endpoints';
import { EntityType } from 'UI/constants/entityTypes';
import { UIStatus } from 'UI/constants/status';
import { OptionRenderers, Renderers } from 'UI/utils/renderers';

import { useStyles } from '../../styles';

const fieldsConfig = [
  {
    name: 'hiringId',
    validation: { required: 'Hiring Authority is required' }
  }
];

type LinkHiringAuthorityProps = {
  candidateId: number,
  onClose: () => any,
  onCompleted: () => any
};

const candidateIncludes = 'personalInformation,specialty,subspecialty,position';

const LinkHiringAuthorityDrawer = ({
  candidateId,
  onClose,
  onCompleted
}: LinkHiringAuthorityProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [uiState, setUiState] = useState<DrawerUiState>({
    [UIStatus.Loading]: false,
    [UIStatus.Saving]: false,
    [UIStatus.Success]: false,
    navStack: []
  });

  const { state } = useFetchWithStatus(
    candidateId ? `${Endpoints.Candidates}/${candidateId}?includes=${candidateIncludes}` : '',
    null,
    null,
    2
  );
  const candidate = state?.results;

  const { status } = state;

  const form = useForm();
  const { handleSubmit, register, unregister, setValue, errors } = form;
  const [comboValues, setComboValues] = useState<Map>({});

  useEffect(() => {
    fieldsConfig.forEach(({ name, type, validation }) => {
      register({ name, type }, validation);
    });

    return () => unregister(fieldsConfig.map(({ name }) => name));
  }, [register, unregister]);

  const handleComboChange = (name?: string, value: any) => {
    setComboValues((prevState: Map): Map => ({ ...prevState, [name]: value }));
    setValue(name, value ? value.id : value, true);
  };

  const createHiringAuthority = async (formData = null) => {
    setUiState(prevState => ({ ...prevState, [UIStatus.Saving]: true }));

    const { hiringId } = formData;
    const url = `${Endpoints.Candidates}/${candidateId}/hiring-authorities/${hiringId}/link`;
    const result = await LinkHiringAuthorityOrCandidate(url, formData, {
      successTitle: 'Hiring Authority Link Successfully',
      successBody: `${candidate?.personalInformation?.full_name} was linked from the Candidate Profile`
    });

    setUiState(prevState => ({ ...prevState, [UIStatus.Saving]: false }));
    result.alert && result.alert.body && dispatch(showAlert(result.alert));
    result.success && onCompleted && onCompleted(result.data);
    result.success && onClose && onClose();
  };

  const onSubmit = async (formData: any) => {
    const candidateName = candidate?.personalInformation?.full_name;
    const hiringName = comboValues?.hiringId?.full_name;

    dispatch(
      confirm({
        severity: 'warning',
        title: strings.inventoryProfiles.sections.candidates.linkCandidateDialog.title,
        message: strings.formatString(
          strings.inventoryProfiles.sections.candidates.linkCandidateDialog.message,
          {
            candidateName,
            hiringName
          }
        ),
        confirmButtonText: 'Link profiles',
        cancelButtonText: 'Cancel',
        onConfirm: async ok => {
          if (!ok) return;
          await createHiringAuthority(formData);
        }
      })
    );
  };

  const buttonDefinitions = buildCloseAndSaveButtonDefinition({
    onClose,
    isSaveDisabled: uiState[UIStatus.Saving],
    isSaving: uiState[UIStatus.Saving]
  });

  return (
    <FPDrawer
      FPDrawerProps={{ open: true }}
      CardHeaderProps={{ title: strings.inventoryProfiles.candidateProfile.linkHiringAuthority }}
      ContentWrapper="form"
      ContentWrapperProps={{ onSubmit: handleSubmit(onSubmit) }}
      onClose={onClose}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      <StatusManager status={status}>
        {candidate && (
          <FormContext {...form}>
            <AutocompleteSelect
              name="hiringId"
              displayKey="full_name"
              selectedValue={comboValues.hiringId}
              error={!!errors.hiringId}
              errorText={errors.hiringId && errors.hiringId.message}
              placeholder="Hiring Authority *"
              typeahead
              typeaheadLimit={50}
              typeaheadParams={{ entityType: EntityType.HiringAuthority }}
              url={Endpoints.Search}
              noOptionsText="Hiring Authorities not found"
              onSelect={handleComboChange}
              renderOption={OptionRenderers.displayHiringAuthority}
            />
          </FormContext>
        )}
        {comboValues?.hiringId && (
          <div className={classes.details}>
            {Renderers.displayBasicInfoEntity(comboValues?.hiringId)}
            {Renderers.externalLink && (
              <div className={classes.link}>
                {Renderers.externalLink(
                  EntityRoutes.HiringAuthorityProfile.replace(':id', comboValues?.hiringId.id),
                  'View Hiring Authority'
                )}
              </div>
            )}
          </div>
        )}
      </StatusManager>
    </FPDrawer>
  );
};

LinkHiringAuthorityDrawer.defaultProps = {
  candidateId: null
};

export default LinkHiringAuthorityDrawer;
